import _async_to_generator from "@swc/helpers/src/_async_to_generator.mjs";
import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _object_without_properties from "@swc/helpers/src/_object_without_properties.mjs";
import _sliced_to_array from "@swc/helpers/src/_sliced_to_array.mjs";
import _ts_generator from "@swc/helpers/src/_ts_generator.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, createColumnHelper, factory, Table, Tooltip, useDisclosure, useProps, useStyles, useTable, useUncontrolled } from "@components/mantine";
import { AccessLevel, Platform, useQuery } from "@core/api";
import { CheckSize16Px, CrossSize16Px } from "@coveord/plasma-react-icons";
import { useMemo, useState } from "react";
import { Locales } from "../../../strings/Locales.js";
import { AccessTableContextProvider, useAccessTableContext } from "../AccessTable.context.js";
import { AccessTableAccessLevelCell } from "../AccessTableAccessLevelCell.js";
import { AccessTableUtils } from "../AccessTableUtils.js";
import classes from "./GroupsAccessTable.module.css";
import { GroupsAccessTableUtils } from "./GroupsAccessTableUtils.js";
import { LoseAccessPrompt } from "./LoseAccessPrompt.js";
import { AccessTableConstants } from "../AccessTableConstants.js";
var defaultProps = {};
export var GroupsAccessTable = factory(function(_props, ref) {
    var _ref = useProps("GroupsAccessTable", defaultProps, _props), resourceId = _ref.resourceId, targetDomain = _ref.privilegeTargetDomain, owner = _ref.privilegeOwner, onChange = _ref.onChange, onInitialization = _ref.onInitialization, data = _ref.data, value = _ref.value, defaultValue = _ref.defaultValue, canEditAll = _ref.canEditAll, loading = _ref.loading, classNames = _ref.classNames, styles = _ref.styles, vars = _ref.vars, className = _ref.className, style = _ref.style, readOnly = _ref.readOnly, others = _object_without_properties(_ref, [
        "resourceId",
        "privilegeTargetDomain",
        "privilegeOwner",
        "onChange",
        "onInitialization",
        "data",
        "value",
        "defaultValue",
        "canEditAll",
        "loading",
        "classNames",
        "styles",
        "vars",
        "className",
        "style",
        "readOnly"
    ]);
    var _$ref = _sliced_to_array(useUncontrolled({
        value: value,
        defaultValue: defaultValue,
        finalValue: null,
        onChange: onChange
    }), 3), _value = _$ref[0], handleChange = _$ref[1], isControlled = _$ref[2];
    var _$ref1 = _sliced_to_array(useState(_value), 2), previousSelection = _$ref1[0], setPreviousSelection = _$ref1[1];
    var _$ref2 = _sliced_to_array(useState(false), 2), isLoseAccessAcknowledged = _$ref2[0], setIsLoseAccessAcknowledged = _$ref2[1];
    var _$ref3 = _sliced_to_array(useDisclosure(false), 2), isLoseAccessPromptOpened = _$ref3[0], _$ref4 = _$ref3[1], openLoseAccessPrompt = _$ref4.open, closeLoseAccessPrompt = _$ref4.close;
    var table = useTable({
        initialState: {
            totalEntries: isControlled && data ? data.length : undefined
        }
    });
    var query = useQuery({
        enabled: !isControlled,
        queryKey: [
            "access",
            "groups",
            {
                targetDomain: targetDomain,
                owner: owner
            }
        ],
        queryFn: /*#__PURE__*/ _async_to_generator(function() {
            var accessModels, accessModelsUpdatedByValue, initialAccessModels, initialValue, error;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        _state.trys.push([
                            0,
                            2,
                            ,
                            3
                        ]);
                        return [
                            4,
                            Platform.organizationAccess.getGroups({
                                accessLevel: [
                                    AccessLevel.CUSTOM,
                                    AccessLevel.VIEW_ALL,
                                    AccessLevel.NONE,
                                    AccessLevel.EDIT_ALL
                                ],
                                privilegeOwner: owner,
                                privilegeTargetDomain: targetDomain
                            })
                        ];
                    case 1:
                        accessModels = _state.sent();
                        table.setTotalEntries(accessModels.length);
                        accessModelsUpdatedByValue = AccessTableUtils.overrideAccessModelsWithValue(accessModels, _value, resourceId);
                        initialAccessModels = GroupsAccessTableUtils.preventGroupLockout(accessModelsUpdatedByValue, resourceId);
                        initialValue = AccessTableUtils.serializeAccessModels(initialAccessModels, resourceId);
                        internalOnChange(initialValue, initialAccessModels);
                        onInitialization === null || onInitialization === void 0 ? void 0 : onInitialization(initialValue);
                        return [
                            2,
                            accessModels
                        ];
                    case 2:
                        error = _state.sent();
                        console.error(error);
                        return [
                            2,
                            []
                        ];
                    case 3:
                        return [
                            2
                        ];
                }
            });
        })
    });
    var rows = useMemo(function() {
        var accessModels = isControlled ? data : query.data;
        return accessModels ? AccessTableUtils.sortModels(accessModels) : undefined;
    }, [
        isControlled,
        data,
        query.data
    ]);
    var internalOnChange = function(newValue) {
        var accessModels = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : rows;
        var wasPartOfGroupThatCanEdit = GroupsAccessTableUtils.isPartOfGroupThatCanEdit(accessModels, resourceId);
        var currentData = AccessTableUtils.overrideAccessModelsWithValue(accessModels, newValue, resourceId);
        var isPartOfGroupThatCanEdit = GroupsAccessTableUtils.isPartOfGroupThatCanEdit(currentData, resourceId);
        if (isPartOfGroupThatCanEdit) {
            // If the user is part of a group that can edit, we don't need to show the prompt
            // We can assume the current state as a safe for the user, and make sure he can come back to it
            setPreviousSelection(newValue);
            setIsLoseAccessAcknowledged(false);
        }
        if (!readOnly && !canEditAll && !isPartOfGroupThatCanEdit && !isLoseAccessAcknowledged && wasPartOfGroupThatCanEdit) {
            openLoseAccessPrompt();
        }
        if ((newValue === null || newValue === void 0 ? void 0 : newValue.length) > 0) {
            handleChange(newValue.sort(AccessTableUtils.sortIdNameModels));
        } else {
            handleChange(AccessTableConstants.noAccess);
        }
    };
    var getStyles = useStyles({
        name: "GroupsAccessTable",
        classes: classes,
        vars: vars,
        classNames: classNames,
        className: className,
        style: style,
        props: _props,
        styles: styles
    });
    return /*#__PURE__*/ _jsxs(AccessTableContextProvider, {
        value: {
            getStyles: getStyles,
            targetDomain: targetDomain,
            value: _value,
            onChange: internalOnChange,
            readOnly: readOnly
        },
        children: [
            /*#__PURE__*/ _jsx(Table, _object_spread({
                ref: ref,
                store: table,
                data: rows,
                loading: isControlled ? loading : query.isLoading || _value === null,
                getRowId: function(row) {
                    return row.id;
                },
                columns: columns
            }, others)),
            /*#__PURE__*/ _jsx(LoseAccessPrompt, {
                opened: isLoseAccessPromptOpened,
                onCancellation: function() {
                    internalOnChange(previousSelection);
                    closeLoseAccessPrompt();
                },
                onLoseAccessConfirmation: function() {
                    setIsLoseAccessAcknowledged(true);
                    closeLoseAccessPrompt();
                }
            })
        ]
    });
});
var columnHelper = createColumnHelper();
var columns = [
    columnHelper.accessor("displayName", {
        header: Locales.format("GroupsAccessTable.headerLabel.group"),
        enableSorting: false
    }),
    columnHelper.accessor("callerPartOf", {
        header: Locales.format("GroupsAccessTable.headerLabel.membership"),
        cell: function(info) {
            return /*#__PURE__*/ _jsx(MembershipCell, {
                callerPartOf: info.getValue()
            });
        },
        enableSorting: false
    }),
    columnHelper.accessor("accessLevel", {
        header: Locales.format("AccessTable.headerLabel.accessLevel"),
        enableSorting: false,
        cell: function(info) {
            return /*#__PURE__*/ _jsx(AccessTableAccessLevelCell, _object_spread({}, info.row.original));
        }
    })
];
var MembershipCell = function(param) {
    var callerPartOf = param.callerPartOf;
    var getStyles = useAccessTableContext().getStyles;
    return /*#__PURE__*/ _jsx(Tooltip, _object_spread_props(_object_spread({
        label: Locales.format(callerPartOf ? "GroupsAccessTable.tooltip.callerPartOf_yes" : "GroupsAccessTable.tooltip.callerPartOf_no"),
        position: "top-start"
    }, getStyles("membershipTooltip")), {
        children: /*#__PURE__*/ _jsx(Box, {
            children: callerPartOf ? /*#__PURE__*/ _jsx(CheckSize16Px, _object_spread({
                height: 16,
                className: classes.iconMember
            }, getStyles("iconMember"))) : /*#__PURE__*/ _jsx(CrossSize16Px, _object_spread({
                height: 16,
                className: classes.iconNotMember
            }, getStyles("iconNotMember")))
        })
    }));
};

//# sourceMappingURL=GroupsAccessTable.js.map